import type {
  DashboardSession,
  SkillProposalReviewAction,
  SkillProposalReviewRequest,
  SkillProposalScope,
  SkillProposalSource,
  SkillProposalStatus,
} from '../../apps/apis/claudeCodeDashboard/types';

const EMPLOYEE_REVIEW_ACTIONS = new Set<SkillProposalReviewAction>(['approve', 'request_revision']);

interface SkillProposalFilterState {
  status: '' | SkillProposalStatus;
  proposalSource: '' | SkillProposalSource;
  employeeId: string;
  role: string;
  limit: number;
}

export const employeeIdForSession = (session: Pick<DashboardSession, 'employeeId' | 'email'>): string =>
  (session.employeeId || session.email).toLowerCase();

export const buildSkillProposalQueryParams = (session: DashboardSession, filters: SkillProposalFilterState) => {
  const statusFilter = filters.status ? { status: filters.status } : {};
  if (!session.isAdmin) {
    return {
      ...statusFilter,
      proposalSource: 'local_submission',
      employeeId: employeeIdForSession(session),
      limit: filters.limit,
    };
  }
  const employeeFilter = filters.employeeId.trim();
  const roleFilter = filters.role.trim();
  return {
    ...statusFilter,
    ...(filters.proposalSource ? { proposalSource: filters.proposalSource } : {}),
    ...(employeeFilter ? { employeeId: employeeFilter } : {}),
    ...(roleFilter ? { role: roleFilter } : {}),
    limit: filters.limit,
  };
};

export const reviewActionsForSession = (
  session: DashboardSession,
  actions: SkillProposalReviewAction[],
): SkillProposalReviewAction[] => {
  if (session.isAdmin) {
    return actions;
  }
  return actions.filter((action) => EMPLOYEE_REVIEW_ACTIONS.has(action));
};

export const isSkillProposalReviewable = (status: SkillProposalStatus): boolean => status === 'pending_review';

export const buildSkillProposalReviewRequest = ({
  proposalId,
  session,
  action,
  note,
  finalScope,
  selectedRoles,
}: {
  proposalId: string;
  session: DashboardSession;
  action: SkillProposalReviewAction;
  note?: string;
  finalScope: SkillProposalScope;
  selectedRoles: string[];
}): SkillProposalReviewRequest => ({
  proposalId,
  action,
  ...(note ? { note } : {}),
  finalScope: session.isAdmin ? finalScope : 'personal',
  selectedRoles: session.isAdmin ? selectedRoles : [],
});
